import { FormEvent, useCallback, useEffect, useState } from "react";
import * as adminRoleService from "../services/adminRoleService";
import type { Permission, Role } from "../types/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { useToast } from "../components/ui/use-toast";
import { useTranslation } from "react-i18next";

const AdminRolesPage = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [createForm, setCreateForm] = useState({
    name: "",
    label: "",
    description: "",
    permissions: [] as string[],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [roleData, permissionData] = await Promise.all([
        adminRoleService.listRoles(),
        adminRoleService.listPermissions(),
      ]);
      setRoles(roleData);
      setPermissions(permissionData);
    } catch (err) {
      toast({
        title: t("errors.generic"),
        description:
          (err as string) ??
          t("admin.roles.errors.loadRoles"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateRole = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const role = await adminRoleService.createRole(createForm);
      setRoles((prev) => [...prev, role as Role]);
      setCreateForm({ name: "", label: "", description: "", permissions: [] });
      toast({
        title: t("admin.roles.toasts.createdTitle"),
        description: t(
          "admin.roles.toasts.createdDescription",
          "{{label}} est disponible.",
          { label: role.label }
        ),
        variant: "success",
      });
      await fetchData();
    } catch (err) {
      toast({
        title: t("errors.generic"),
        description:
          (err as string) ??
          t("admin.roles.errors.createRole"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTogglePermission = async (role: Role, permissionKey: string) => {
    const current = new Set(role.permissions);
    current.has(permissionKey)
      ? current.delete(permissionKey)
      : current.add(permissionKey);
    const nextPermissions = Array.from(current);
    try {
      const updated = await adminRoleService.updateRolePermissions(
        role.id,
        nextPermissions
      );
      setRoles((prev) =>
        prev.map((r) => (r.id === role.id ? (updated as Role) : r))
      );
      toast({
        title: t(
          "admin.roles.toasts.permissionsUpdatedTitle",
            "Permissions updated"
        ),
        description: t(
          "admin.roles.toasts.permissionsUpdatedDescription",
          "{{label}} dispose maintenant de {{count}} permissions.",
          { label: role.label, count: nextPermissions.length }
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        title: t("errors.generic"),
        description:
          (err as string) ??
          t(
            "admin.roles.errors.updatePermissions",
            "Impossible de mettre a jour les permissions."
          ),
        variant: "destructive",
      });
    }
  };

  const handleCreatePermissionToggle = (
    permissionKey: string,
    checked: boolean
  ) => {
    setCreateForm((prev) => {
      const set = new Set(prev.permissions);
      checked ? set.add(permissionKey) : set.delete(permissionKey);
      return { ...prev, permissions: Array.from(set) };
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground dark:text-white">
          {t("admin.roles.title")}
        </h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          {t(
            "admin.roles.subtitle",
            "Configurez des roles metiers et attribuez leurs acces en un clic."
          )}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {roles.map((role) => (
          <article
            key={role.id}
            className="rounded-3xl border border-border bg-card p-5 shadow-lg dark:border-white/10 dark:bg-white/5 dark:shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground dark:text-white">
                  {role.label}
                </h3>
                <p className="text-xs uppercase tracking-wide text-muted-foreground dark:text-white/50">
                  {role.name}
                </p>
              </div>
              <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground dark:bg-white/10 dark:text-white/70">
                {t("admin.roles.permissionsCount", {
                  count: role.permissions.length,
                })}
              </span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground dark:text-white/70">
              {role.description ??
                t("admin.roles.noDescription")}
            </p>
            <div className="mt-4 space-y-2">
              {permissions.map((permission) => (
                <label
                  key={permission.id}
                  className="flex items-center justify-between rounded-2xl border border-border bg-muted/40 px-3 py-2 text-sm text-foreground/90 dark:border-white/10 dark:bg-black/20 dark:text-white/80">
                  <span>{permission.label}</span>
                  <Checkbox
                    checked={role.permissions.includes(permission.key)}
                    onCheckedChange={() =>
                      handleTogglePermission(role, permission.key)
                    }
                    className="h-5 w-5 rounded-lg border-border dark:border-white/30"
                  />
                </label>
              ))}
            </div>
          </article>
        ))}
      </div>

      <form
        onSubmit={handleCreateRole}
        className="space-y-4 rounded-3xl border border-border bg-card p-6 dark:border-white/10 dark:bg-white/5">
        <h3 className="text-lg font-semibold text-foreground dark:text-white">
          {t("admin.roles.createTitle")}
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("admin.roles.internalName")}</Label>
            <Input
              value={createForm.name}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, name: event.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label>{t("admin.roles.label")}</Label>
            <Input
              value={createForm.label}
              onChange={(event) =>
                setCreateForm((prev) => ({
                  ...prev,
                  label: event.target.value,
                }))
              }
              required
            />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>{t("admin.roles.description")}</Label>
            <Textarea
              value={createForm.description}
              onChange={(event) =>
                setCreateForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              placeholder={t(
                "admin.roles.descriptionPlaceholder",
                "Decrivez l'usage du role..."
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("admin.roles.permissions")}</Label>
          <div className="grid gap-2 md:grid-cols-2">
            {permissions.map((permission) => (
              <label
                key={permission.id}
                className="flex items-center justify-between rounded-2xl border border-border bg-muted/40 px-3 py-2 text-sm text-foreground/90 dark:border-white/10 dark:bg-black/20 dark:text-white/80">
                <span>{permission.label}</span>
                <Checkbox
                  checked={createForm.permissions.includes(permission.key)}
                  onCheckedChange={(checked) =>
                    handleCreatePermissionToggle(
                      permission.key,
                      Boolean(checked)
                    )
                  }
                  className="h-5 w-5 rounded-lg border-border dark:border-white/30"
                />
              </label>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? t("admin.roles.creating")
            : t("admin.roles.createButton")}
        </Button>
      </form>
    </div>
  );
};

export default AdminRolesPage;
